import { create } from "zustand";
import { persist } from "zustand/middleware";
import { calculateSuggestedCatTraySize, IMealPlan } from "@./cat-calculations";
import dayjs from "dayjs";
import { CustomerAPI } from "libs/api/customer/src/lib/CustomerAPI";
import { IFreshOptionsNewCustomerRequest } from "@api-clients/customer";

import {
  ISignupFlowStore,
  ICatFormData,
  CatFieldName,
  getAvailableFreshPlansRequest,
} from "../types";
import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  defaultViewState,
} from "../constants";
import {
  getNestedFieldValue,
  updateNestedField,
  removeNestedField,
  transformCatDataForApi,
  calculateCatBirthday,
} from "../utils";

const customerApi = new CustomerAPI(
  // eslint-disable-next-line @typescript-eslint/dot-notation
  process.env["NEXT_PUBLIC_OLD_API_BASE_URL"] as string,
);

/**
 * Zustand store for managing sign-up flow data: cats, user, delivery, and subscription.
*/
export const useSignupFlowStore = create<ISignupFlowStore>()(
  persist(
    (set, get) => ({
      /* --- Initialisation ---*/
      isStoreInitialised: false,
      setIsStoreInitialised: (value) => set({ isStoreInitialised: value }),

      /* --- Cats ---*/
      cats: {},
      activeCatId: "",

      activeCat: () => get().cats[get().activeCatId],

      getCatSuggestedTraySize: (catId: string): IMealPlan | undefined => {
        const cat = get().cats[catId];
        if (!cat?.age) { return; }
        const weight = cat.weight?.valueInKg;
        const activityLevel = cat.body?.activityLevel;
        const bodyType = cat.body?.bodyType;
        const age = {
          years: cat.age.years,
          months: cat.age.months,
          weeks: cat.age.weeks,
        };
        if (!weight || !activityLevel || !bodyType || !age) { return; }
        return calculateSuggestedCatTraySize(weight, bodyType, activityLevel, age);

      },

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat, viewState: defaultViewState },
          },
        };
      }),

      createCats: (entries) => set((state) => {
        const updatedCats: Record<string, ICatFormData> = { ...state.cats };
        const incomingIds = new Set(entries.map((e) => e.id));

        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
          }
        }
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...(updatedCats[id] ?? {}),
            ...cat,
          };
        }

        const firstCatId = entries[0]?.id ?? "";

        for (const { id } of entries) {
          if (!updatedCats[id]?.viewState) {
            updatedCats[id] = { ...updatedCats[id], viewState: defaultViewState };
          }
        }

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
        };
      }),

      updateCat: (id, cat) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], ...cat },
        },
      })),
      getCat: (id) => get().cats[id],
      getActiveCatIds: () => Object.keys(get().cats).filter((catId) => !get().cats[catId].softDelete),

      softDeleteCat: (id) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: true },
        },
      })),

      restoreCat: (id) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: false },
        },
      })),

      migrateCatData: (fieldNames: CatFieldName[], sourceId: string, targetIds: string[]) => set((state) => {
        const source = state.cats[sourceId];
        if (!source) { return {}; }

        const updatedCats = { ...state.cats };

        const updateTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            const sourceValue = getNestedFieldValue(source, fieldName);
            if (sourceValue !== undefined) {
              target = updateNestedField(target, fieldName, sourceValue);
            }
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          updateTargetCat(targetId);
        }

        return { cats: updatedCats };
      }),

      revertMigrateCatData: (fieldNames: CatFieldName[], targetIds: string[]) => set((state) => {
        const updatedCats = { ...state.cats };

        const removeFromTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            target = removeNestedField(target, fieldName);
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          removeFromTargetCat(targetId);
        }

        return { cats: updatedCats };
      }),

      setActiveCatId: (id) => set({ activeCatId: id }),

      /* --- User ---*/
      user: defaultUser,
      updateUser: (data) => set((state) => ({
        user: {
          ...state.user,
          ...data,
        },
      })),

      /* --- Delivery ---*/
      delivery: defaultDelivery,
      updateDelivery: (data) => set((state) => ({
        delivery: {
          ...state.delivery,
          ...data,
        },
      })),

      /* --- Subscription ---*/
      subscription: defaultSubscription,
      updateSubscription: (data) => set((state) => ({
        subscription: {
          ...state.subscription,
          ...data,
        },
      })),

      /* --- View State ---*/
      updateViewState: (catId, view, params) => set((state) => {
        const currentCat = state.cats[catId];
        if (!currentCat) { return {}; }

        const currentViewState = currentCat.viewState || defaultViewState;
        return {
          cats: {
            ...state.cats,
            [catId]: {
              ...currentCat,
              viewState: {
                ...currentViewState,
                [view]: { ...currentViewState[view], ...params },
              },
            },
          },
        };
      }),

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        subscription: defaultSubscription,
      }),

      /* --- Call getAvailableFreshPlans and update store ---*/
      callGetAvailableFreshPlansAndUpdateStore: async (requestedProps: getAvailableFreshPlansRequest) => {
        const { user } = get();

        // Build the base request object from store data
        const baseRequestObj: IFreshOptionsNewCustomerRequest = {
          deliveryDate: dayjs(get().delivery.deliveryDate, "YYYY-MM-DD").toString(),
          discountCode: get().subscription.discount,
          trialType: "TWENTY_OFF", // this will eventually be removed (tech-340 ticket)
          discountNibbles: undefined,
          catData: transformCatDataForApi(get().cats, get().subscription),
        };

        const mergedRequestObj = { ...baseRequestObj, ...requestedProps };

        try {
          const response = await customerApi.getAvailableFreshPlans(user, mergedRequestObj);
          if (response) {
            const { deliveryDate, totalTrialDiscount, totalTrialNoDiscount, discount } = response;
            set({
              delivery: {
                ...get().delivery,
                deliveryDate: new Date(deliveryDate),
              },
              subscription: {
                ...get().subscription,
                trialTotalOriginalPrice: totalTrialNoDiscount.totalPrice,
                trialTotalDiscountedPrice: totalTrialDiscount.totalPrice,
                discount: discount?.code ?? "",
              },
            });
          }
          return null;
        } catch (error) {
          return error;
        }
      },

      /* --- Birthday calculation ---*/
      calculateAllCatsBirthdays: () => set((state) => {
        const updatedCats = { ...state.cats };

        Object.entries(updatedCats).forEach(([ catId, cat ]) => {
          if (!cat.softDelete) {
            const calculatedBirthday = calculateCatBirthday(cat);
            if (calculatedBirthday) {
              updatedCats[catId] = {
                ...cat,
                birthday: calculatedBirthday,
              };
            }
          }
        });

        return { cats: updatedCats };
      }),
    }),
    {
      name: "suf-store",
      onRehydrateStorage: () => (state) => {
        state?.setIsStoreInitialised(true);
      },
    },
  ),
);
